import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import Clock from '@deliveroo/icon-set/react/Clock';
import Stopwatch from '@deliveroo/icon-set/react/Stopwatch';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Modal from '@deliveroo/consumer-component-library/components/Modal';
import RadioInput from '@deliveroo/consumer-component-library/components/RadioInput';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import Row from '@deliveroo/consumer-component-library/components/Row';
import Select from '@deliveroo/consumer-component-library/components/Select';
import Span from '@deliveroo/consumer-component-library/components/Span';

import i18n from '../../utils/i18n';
import { FULFILLMENT_TIME_ASAP } from '../../utils/constants';

import FetchErrorContent from '../FetchErrorContent';

import styles from './styles.scss';

const FULFILLMENT_TYPE_ASAP = 'FULFILLMENT_TYPE_ASAP';
const FULFILLMENT_TYPE_LATER = 'FULFILLMENT_TYPE_LATER';

class HomeDateTimePicker extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  componentDidMount() {
    this.props.onOpen();
  }

  getInitialState = () => {
    const { fulfillmentDay, fulfillmentTime } = this.props;
    const isAsap = fulfillmentTime === FULFILLMENT_TIME_ASAP;
    const typeOption = isAsap ? FULFILLMENT_TYPE_ASAP : FULFILLMENT_TYPE_LATER;
    return { typeOption, dayOption: fulfillmentDay, timeOption: fulfillmentTime };
  };

  onChangeType = (typeOption) => {
    const { fulfillmentTimeOptionsMap } = this.props;
    const { dayOption } = this.state;
    const timeOption =
      typeOption === FULFILLMENT_TYPE_ASAP
        ? FULFILLMENT_TIME_ASAP
        : fulfillmentTimeOptionsMap[dayOption][0].value;
    this.setState({ typeOption, timeOption });
  };

  onChangeDay = (dayOption) => {
    const { fulfillmentTimeOptionsMap } = this.props;
    const fulfillmentTimeOptions = fulfillmentTimeOptionsMap[dayOption];
    const timeOption = fulfillmentTimeOptions[0].value;
    this.setState({ dayOption, timeOption });
  };

  onChangeTime = (timeOption) => this.setState({ timeOption });

  submitFulfillmentTimeChange = (hasError, hasChanged) => {
    if (hasError) {
      this.props.requestFulfillmentTimes();
    } else if (!hasChanged) {
      this.props.onClose();
    }

    this.props.setHomeFulfillmentTime(this.state.timeOption);
    this.props.onConfirm();
  };

  render() {
    const {
      fulfillmentTime,
      fulfillmentDayOptions,
      fulfillmentTimeOptionsMap,
      isAsapUnavailable,
      withClickAndCollect,
      onClose,
    } = this.props;

    const { typeOption, dayOption, timeOption } = this.state;

    const fulfillmentTimeOptions = fulfillmentTimeOptionsMap[dayOption];
    const dayOptionLabel = fulfillmentDayOptions.find(({ value }) => value === dayOption).label;
    const timeOptionLabel =
      timeOption !== FULFILLMENT_TIME_ASAP
        ? fulfillmentTimeOptions.find(({ value }) => value === timeOption).label
        : '';

    const hasChanged = timeOption !== fulfillmentTime;
    const hasError =
      !fulfillmentDayOptions ||
      !fulfillmentTimeOptions ||
      fulfillmentDayOptions.length === 0 ||
      fulfillmentTimeOptions.length === 0;

    return (
      <Modal
        isOpen
        appElement="#__next"
        type="fullscreen"
        onClose={onClose}
        closeButtonLabel={i18n.gettext('closeButton.label')}
        headerTitle={
          withClickAndCollect
            ? i18n.gettext('restaurant_list.time_picker.modalTitle')
            : i18n.gettext('restaurant_list.time_picker.modal.title')
        }
        renderFooter={() => (
          <Fragment>
            <div className={styles.cancelButtonWrapper}>
              <Button type="secondary" isBlock spacing="0 medium 0 0" onClick={onClose}>
                {i18n.gettext('restaurant_list.modal.cancel')}
              </Button>
            </div>
            <div className={styles.confirmButtonWrapper}>
              <Button
                type="primary"
                isBlock
                onClick={() => this.submitFulfillmentTimeChange(hasError, hasChanged)}
              >
                {hasError
                  ? i18n.gettext('home.date_modal.error.btn')
                  : i18n.gettext('restaurant_list.modal.confirm')}
              </Button>
            </div>
          </Fragment>
        )}
      >
        <div id="date-time-modal">
          {hasError && (
            <FetchErrorContent
              heading={i18n.gettext('home.date_modal.error.heading')}
              message={i18n.gettext('home.date_modal.error.message')}
            />
          )}
          {!hasError && (
            <Fragment>
              <RadioInput
                isBlock
                hasLabelBefore
                name="fulfillment-type"
                value={FULFILLMENT_TYPE_ASAP}
                isChecked={!isAsapUnavailable && typeOption === FULFILLMENT_TYPE_ASAP}
                isDisabled={isAsapUnavailable}
                onChange={(name, value) => this.onChangeType(value)}
                label={
                  <div className={styles.radioLabel}>
                    <Row
                      left={
                        <Icon
                          component={Stopwatch}
                          color={isAsapUnavailable ? 'light-grey' : 'grey'}
                        />
                      }
                    >
                      <Span color={isAsapUnavailable ? 'light-grey' : undefined}>
                        {i18n.gettext('restaurant_list.time_picker.deliver_now')}
                      </Span>
                    </Row>
                  </div>
                }
                spacing="small 0"
              />

              <div className={styles.typeSeparator} />

              <RadioInput
                isBlock
                hasLabelBefore
                name="fulfillment-type"
                value={FULFILLMENT_TYPE_LATER}
                isChecked={typeOption === FULFILLMENT_TYPE_LATER}
                onChange={(name, value) => this.onChangeType(value)}
                label={
                  <div className={styles.radioLabel}>
                    <Row left={<Icon component={Clock} color="grey" />}>
                      {i18n.gettext('restaurant_list.time_picker.schedule')}
                    </Row>
                  </div>
                }
                spacing="small 0"
              />

              {typeOption === FULFILLMENT_TYPE_LATER && (
                <div className={styles.stackableRow}>
                  {fulfillmentDayOptions.length > 0 && (
                    <div className={styles.stackableCell}>
                      <Select
                        isBlock
                        value={dayOption}
                        selectedOptionText={dayOptionLabel}
                        onChange={this.onChangeDay}
                        spacing="medium 0 0 small"
                      >
                        {fulfillmentDayOptions.map(({ label, value }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}

                  {fulfillmentTimeOptions.length > 0 && (
                    <div className={styles.stackableCell}>
                      <Select
                        isBlock
                        value={timeOption}
                        selectedOptionText={timeOptionLabel}
                        onChange={this.onChangeTime}
                        spacing="medium 0 0 small"
                      >
                        {fulfillmentTimeOptions.map(({ label, value }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </Fragment>
          )}
        </div>
      </Modal>
    );
  }
}

const selectOptionType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

const selectOptionsType = PropTypes.arrayOf(selectOptionType);

HomeDateTimePicker.propTypes = {
  fulfillmentDay: PropTypes.string.isRequired,
  fulfillmentTime: PropTypes.string.isRequired,
  fulfillmentDayOptions: selectOptionsType.isRequired,
  fulfillmentTimeOptionsMap: PropTypes.objectOf(selectOptionsType).isRequired,
  isAsapUnavailable: PropTypes.bool.isRequired,
  requestFulfillmentTimes: PropTypes.func.isRequired,
  setHomeFulfillmentTime: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  withClickAndCollect: PropTypes.bool,
};

HomeDateTimePicker.defaultProps = {
  withClickAndCollect: false,
};

export default HomeDateTimePicker;
